import React, { useState, useMemo } from 'react';
import { FaChevronDown, FaChevronRight, FaCheckCircle, FaExclamationCircle, FaArrowLeft } from 'react-icons/fa';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';

const TestResultsView = ({ results, customAgent, nodes = [], edges = [], onBack, onClose }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const execution = results?.execution || results;
  const nodeResults = execution?.nodeResults || results?.result || {};
  const executionPath = execution?.executionPath || [];
  const duration = execution?.duration || results?.duration || 0;
  const isRunning = execution?.status === 'running';

  // Build node data with execution status
  const executionNodes = useMemo(() => {
    if (!nodes || nodes.length === 0) return [];

    const executedNodeIds = new Set(executionPath);
    
    return nodes.map(node => {
      const result = nodeResults[node.id];
      const isExecuted = executedNodeIds.has(node.id);
      const hasError = result?.success === false;
      
      return {
        ...node,
        position: node.position || { x: 0, y: 0 },
        data: {
          ...node.data,
          executed: isExecuted,
          result: result,
          status: hasError ? 'failed' : (isExecuted ? 'completed' : 'pending')
        }
      };
    });
  }, [nodes, executionPath, nodeResults]);

  // Build edges with execution status
  const executionEdges = useMemo(() => {
    if (!edges) return [];
    
    const executedNodeIds = new Set(executionPath);
    
    return edges.map(edge => {
      const sourceExecuted = executedNodeIds.has(edge.source);
      const targetExecuted = executedNodeIds.has(edge.target);
      
      return {
        ...edge,
        animated: sourceExecuted && targetExecuted,
        style: {
          stroke: sourceExecuted && targetExecuted ? '#10b981' : '#94a3b8',
          strokeWidth: sourceExecuted && targetExecuted ? 2 : 1
        }
      };
    });
  }, [edges, executionPath]);

  const toggleNodeExpand = (nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatJSON = (data) => {
    if (!data) return 'No data';
    try {
      return JSON.stringify(data, null, 2);
    } catch (err) {
      return String(data);
    }
  };

  const truncateData = (data, maxLength = 200) => {
    const str = typeof data === 'string' ? data : formatJSON(data);
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
  };

  // Get input data for a node
  const getNodeInput = (nodeId, nodeIndex) => {
    if (nodeIndex === 0) {
      // First node (trigger) gets trigger data
      return execution?.triggerData || {};
    }
    
    // Other nodes get output from previous node
    const prevNodeId = executionPath[nodeIndex - 1];
    if (prevNodeId && nodeResults[prevNodeId]) {
      return nodeResults[prevNodeId];
    }
    
    return null;
  };

  return (
    <div className="test-results-view">
      {/* Execution Summary */}
      <div className="execution-summary">
        <div className="summary-card">
          <div className="summary-label">Status</div>
          <div className={`summary-value status-${execution?.status || 'completed'}`}>
            {isRunning ? (
              <>
                <span className="status-spinner"></span> Running...
              </>
            ) : (
              execution?.status || 'completed'
            )}
          </div>
        </div>
        
        <div className="summary-card">
          <div className="summary-label">Duration</div>
          <div className="summary-value">{formatDuration(duration)}</div>
        </div>
        
        <div className="summary-card">
          <div className="summary-label">Nodes Executed</div>
          <div className="summary-value">
            {executionPath.length} / {nodes.length}
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {execution?.error && (
        <div className="execution-error-banner">
          <FaExclamationCircle />
          <div>
            <strong>Error:</strong> {execution.error}
          </div>
        </div>
      )}

      {/* Node-by-Node Results */}
      <div className="node-results-section">
        <h3>Node Execution Details</h3>
        {isRunning && executionPath.length === 0 && (
          <div className="execution-waiting">
            <div className="loading-spinner"></div>
            <p>Waiting for customAgent execution to start...</p>
          </div>
        )}
        <div className="node-results-list">
          {executionPath.map((nodeId, index) => {
            const node = nodes.find(n => n.id === nodeId);
            if (!node) return null;

            const result = nodeResults[nodeId];
            const input = getNodeInput(nodeId, index);
            const isExpanded = expandedNodes.has(nodeId);
            const hasError = result?.success === false;
            const isCurrentlyExecuting = isRunning && index === executionPath.length - 1 && !result;

            return (
              <div key={nodeId} className={`node-result-card ${hasError ? 'error' : ''} ${isCurrentlyExecuting ? 'executing' : ''}`}>
                <div 
                  className="node-result-header"
                  onClick={() => toggleNodeExpand(nodeId)}
                >
                  <div className="node-result-title">
                    {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    <span className="node-step-number">Step {index + 1}</span>
                    <span className="node-name">{node.data?.label || nodeId}</span>
                    <span className="node-type">({node.data?.nodeType || 'unknown'})</span>
                    {isCurrentlyExecuting ? (
                      <span className="status-icon executing-icon">
                        <span className="executing-spinner"></span> Executing...
                      </span>
                    ) : hasError ? (
                      <FaExclamationCircle className="status-icon error-icon" />
                    ) : (
                      <FaCheckCircle className="status-icon success-icon" />
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="node-result-content">
                    {/* Input Data */}
                    <div className="node-data-section">
                      <h4>Input Data</h4>
                      {input ? (
                        <div className="data-display">
                          <pre>{formatJSON(input)}</pre>
                        </div>
                      ) : (
                        <div className="data-placeholder">Input data will appear here once the node starts executing...</div>
                      )}
                    </div>

                    {/* Output Data */}
                    <div className="node-data-section">
                      <h4>Output Data</h4>
                      {isCurrentlyExecuting ? (
                        <div className="data-placeholder">
                          <span className="executing-spinner"></span> Generating output...
                        </div>
                      ) : hasError ? (
                        <div className="error-display">
                          <strong>Error:</strong> {result.error || 'Unknown error'}
                        </div>
                      ) : result ? (
                        <div className="data-display">
                          <pre>{formatJSON(result)}</pre>
                        </div>
                      ) : (
                        <div className="data-placeholder">Output data will appear here once the node completes...</div>
                      )}
                    </div>

                    {/* Additional Info */}
                    {result && (
                      <div className="node-meta-info">
                        {result.data && (
                          <div className="meta-item">
                            <strong>Data Type:</strong> {typeof result.data}
                          </div>
                        )}
                        {result.success !== undefined && (
                          <div className="meta-item">
                            <strong>Success:</strong> {result.success ? 'Yes' : 'No'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="test-results-actions">
        <button className="btn-secondary" onClick={onBack}>
          <FaArrowLeft /> Test Again
        </button>
        <button className="btn-primary" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

export default TestResultsView;
